import type { Phase } from './constants'

/**
 * Maps project phases to the set of feature tab keys that are unlocked.
 * Tab keys match the route segment used in project-tabs.tsx.
 * '*' means all features are available.
 */
export const PHASE_FEATURES: Record<Phase, string[]> = {
  idea: [
    'captain',
    'discussions',
    'comments',
    'polls',
    'crew-manifest',
    'files',
    'bookmarks',
  ],
  viability: [
    'captain',
    'discussions',
    'wiki',
    'comments',
    'polls',
    'crew-manifest',
    'files',
    'bookmarks',
    'surveys',
    'whiteboard',
  ],
  building: ['*'],
  marketing: ['*'],
  live: ['*'],
  scaling: ['*'],
  archived: ['*'],
}

/** The minimum phase needed to unlock each feature (used in tooltips). */
const FEATURE_UNLOCK_PHASE: Record<string, Phase> = {
  surveys: 'viability',
  wiki: 'viability',
  whiteboard: 'viability',
  milestones: 'building',
  kanban: 'building',
  standups: 'building',
  'time-tracking': 'building',
  reviews: 'building',
  retros: 'building',
  okrs: 'building',
  'crew-deck': 'building',
  finances: 'building',
  'content-calendar': 'building',
  onboarding: 'building',
  settings: 'building',
}

/**
 * Returns whether a feature tab is available for the given project phase.
 * The overview tab and any tab not listed in PHASE_FEATURES are always visible.
 */
export function isFeatureAvailable(tabKey: string, phase: Phase): boolean {
  const features = PHASE_FEATURES[phase]
  if (!features) return true
  if (features.includes('*')) return true
  return features.includes(tabKey)
}

/**
 * Returns the phase name needed to unlock a locked feature (for tooltip text).
 * Returns null if the feature is not phase-gated.
 */
export function getUnlockPhase(tabKey: string): Phase | null {
  return FEATURE_UNLOCK_PHASE[tabKey] ?? null
}
